import React, { useState, useMemo } from 'react';
import { history } from '../../utils';
import { addAssigment } from '../../api/hardware';
import IconButtonMenu from '../../components/Redesing/icon-button-menu';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import CustomAsyncToast from '../../components/Redesing/custom-async-toast';
import PageContainer from '../../components/Redesing/page-container';
import CardView from '../../components/Redesing/card-view';
import { Stack } from '@mui/system';
import {
  MovaticFieldGroupAsyncSelect,
  MovaticFieldGroupSelect,
  MovaticFieldItemLink,
} from '../../components/Redesing/movatic-field-group';
import MenuItem from '@mui/material/MenuItem';
import { useHardwareTypesRelayQuery } from '../../hooks/hardware/query/V2/useHardwareTypesRelay';
import { useHardwareProductsRelayQuery } from '../../hooks/hardwareProduct/query/V2/useHardwareProductsRelay';
import {
  useHardwareRelayQuery,
  HardwareGraphQL,
  HardwareQueryFilter,
} from '../../hooks/hardware/query/V2/useHardwareRelay';
import { useHardwareDetailRelayQuery } from '../../hooks/hardware/query/V2/useHardwareDetailRelay';
import { useSystemStore } from '../../store/systemStore';

interface ValidationState {
  result: 'error' | null;
  help: string;
}

interface MatchParams {
  hardwareId: string;
}

interface AddHardwareAssignmentProps {
  match: {
    params: MatchParams;
  };
}

type SearchFilterType =
  | ''
  | 'hardware_id'
  | 'label'
  | 'mac'
  | 'number'
  | 'vendor_id'
  | 'vendor_serial';

const valuesMinSearch: Record<string, number> = {
  mac: 17,
  label: 1,
  number: 1,
  vendor_id: 15,
  hardware_id: 36,
  vendor_serial: 1,
};

const hardwareSearchFilters = [
  { value: '', name: 'Select' },
  { value: 'hardware_id', name: 'ID - (e.g., g1h2i3j4-k1l2-m1n2-o1p2-a1b2c3d4e4f4)' },
  { value: 'label', name: 'Label - (e.g., Hardware Label)' },
  { value: 'mac', name: 'Mac Address - (e.g., 01:23:45:67:89:AB)' },
  { value: 'number', name: 'Number - (e.g., 805)' },
  { value: 'vendor_id', name: 'Vendor ID - (e.g., 123456789123456)' },
  { value: 'vendor_serial', name: 'Vendor Serial Number - (e.g., V2020201138230)' },
];

function AddHardwareAssignment({ match }: AddHardwareAssignmentProps) {
  const parentHardwareId = match.params.hardwareId;
  const systemId = useSystemStore((state) => state.systemInfo.gid);

  const [hardwareChild, setHardwareChild] = useState<HardwareGraphQL | null>(null);
  const [searchFilter, setSearchFilter] = useState<SearchFilterType>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [hardwareTypeId, setHardwareTypeId] = useState<string>('');
  const [hardwareProductId, setHardwareProductId] = useState<string>('');
  const [open, setOpen] = useState(false);

  const [hardwareTypeValidation, setHardwareTypeValidation] = useState<ValidationState>({
    result: null,
    help: '',
  });
  const [searchFilterValidation, setSearchFilterValidation] = useState<ValidationState>({
    result: null,
    help: '',
  });
  const [hardwareChildValidation, setHardwareChildValidation] = useState<ValidationState>(
    {
      result: null,
      help: '',
    }
  );

  // Fetch parent hardware details using Relay
  const { data: parentHardwareData, isLoading: isLoadingParent } =
    useHardwareDetailRelayQuery(parentHardwareId, {
      enabled: !!parentHardwareId,
    });

  // Fetch hardware types using Relay
  const { data: hardwareTypesData, isLoading: isLoadingTypes } =
    useHardwareTypesRelayQuery();

  // Map hardware types to dropdown format
  const hardwareTypesOptions = useMemo(() => {
    if (!hardwareTypesData) return [];
    return hardwareTypesData.map((type) => ({
      name: type.name ?? '',
      value: type.id,
    }));
  }, [hardwareTypesData]);

  // Fetch hardware products based on selected type using Relay
  const { data: hardwareProductsData } = useHardwareProductsRelayQuery(hardwareTypeId, {
    enabled: !!hardwareTypeId,
  });

  // Map hardware products to dropdown format
  const hardwareProductsOptions = useMemo(() => {
    if (!hardwareProductsData) return [];
    return hardwareProductsData
      .filter((product) => product.name !== null)
      .map((product) => ({
        name: product.name ?? '',
        value: product.id,
      }));
  }, [hardwareProductsData]);

  // Build filter for hardware search
  const hardwareFilter = useMemo((): HardwareQueryFilter | undefined => {
    if (!systemId || !hardwareTypeId) return undefined;

    const filter: HardwareQueryFilter = {
      systemId: systemId,
      hardwareTypeId: hardwareTypeId,
    };

    if (hardwareProductId) {
      filter.hardwareProductId = hardwareProductId;
    }

    // Apply search query filter
    if (searchFilter && searchQuery) {
      const minLength = valuesMinSearch[searchFilter] || 1;
      if (searchQuery.length >= minLength) {
        switch (searchFilter) {
          case 'hardware_id':
            // For ID search, we'd need to use the id filter
            // This might need special handling depending on the schema
            break;
          case 'label':
            filter.label = `%${searchQuery}%`; // Will use ilike pattern
            break;
          case 'mac':
            filter.mac = searchQuery;
            break;
          case 'number':
            filter.number = parseInt(searchQuery, 10);
            break;
          case 'vendor_id':
            filter.vendorId = searchQuery;
            break;
          case 'vendor_serial':
            filter.vendorSerial = searchQuery;
            break;
        }
      }
    }

    return filter;
  }, [systemId, hardwareTypeId, hardwareProductId, searchFilter, searchQuery]);

  // Fetch hardware list using Relay
  const { data: hardwareOptions, isLoading: isLoadingHardware } = useHardwareRelayQuery(
    hardwareFilter,
    {
      enabled: !!systemId && !!hardwareTypeId,
      limit: 100,
    }
  );

  // Filter out the parent hardware from options
  const filteredHardwareOptions = useMemo(() => {
    if (!hardwareOptions) return [];
    return hardwareOptions.filter((hw) => hw.id !== parentHardwareId);
  }, [hardwareOptions, parentHardwareId]);

  const createAssignment = () => {
    if (!requiredFieldsValidation()) return;
    if (!hardwareChild) return;

    CustomAsyncToast({
      promise: () =>
        addAssigment({
          hardware_parent_id: parentHardwareId,
          hardware_child_id: hardwareChild.id,
        }),
      successMessage: () => `Hardware ${hardwareChild.id} was assigned successfully`,
      loadingMessage: () => `Assigning hardware ${hardwareChild.id}...`,
      errorMessage: () => `Error assigning hardware ${hardwareChild.id}`,
    }).then((response) => {
      if (response) goBack();
    });
  };

  const goBack = () => {
    history.push(`/hardware/${parentHardwareId}`);
  };

  const getValidationState = (value: unknown): 'error' | null => {
    if (Array.isArray(value)) {
      return value.length <= 0 ? 'error' : null;
    }
    if (typeof value === 'string') {
      return value.length <= 0 ? 'error' : null;
    }
    return value ? null : 'error';
  };

  const requiredFieldsValidation = (): boolean => {
    const hardwareTypeResult: ValidationState = {
      result: null,
      help: '',
    };
    const searchFilterResult: ValidationState = {
      result: null,
      help: '',
    };
    const hardwareChildResult: ValidationState = {
      result: null,
      help: '',
    };
    let validationResult = true;

    if (getValidationState(hardwareTypeId) === 'error') {
      validationResult = false;
      hardwareTypeResult.result = 'error';
      hardwareTypeResult.help = 'Please select a hardware child type.';
    }
    if (getValidationState(searchFilter) === 'error') {
      validationResult = false;
      searchFilterResult.result = 'error';
      searchFilterResult.help = 'Please select a search by.';
    }
    if (getValidationState(hardwareChild) === 'error' && hardwareTypeId) {
      validationResult = false;
      hardwareChildResult.result = 'error';
      hardwareChildResult.help = 'Please select a hardware child.';
    }

    setHardwareTypeValidation(hardwareTypeResult);
    setSearchFilterValidation(searchFilterResult);
    setHardwareChildValidation(hardwareChildResult);
    return validationResult;
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
  };

  const handleChildSelected = (child: HardwareGraphQL | null) => {
    setHardwareChild(child);
    setHardwareChildValidation({ result: null, help: '' });
  };

  const getOptionText = (option: HardwareGraphQL): string => {
    if (searchFilter === 'hardware_id') return option.id;
    const fieldValue = (() => {
      switch (searchFilter) {
        case 'label':
          return option.label;
        case 'mac':
          return option.mac;
        case 'number':
          return option.number?.toString();
        case 'vendor_id':
          return option.vendorId;
        case 'vendor_serial':
          return option.vendorSerial;
        default:
          return '';
      }
    })();
    return `${fieldValue || ''} - ${option.id}`;
  };

  const onHardwareTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.value;
    setSearchFilter('');
    setSearchQuery('');
    setHardwareProductId('');
    setHardwareTypeId(selected);
    setHardwareChild(null);
    setHardwareTypeValidation({ result: null, help: '' });
  };

  const onHardwareProductChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.value;
    setHardwareProductId(selected);
    setSearchFilter('');
    setSearchQuery('');
    setHardwareChild(null);
  };

  const handleSearchFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchType = e.target.value as SearchFilterType;
    setSearchFilter(searchType);
    setSearchQuery('');
    setHardwareChild(null);
    setSearchFilterValidation({ result: null, help: '' });
  };

  const isLoading = isLoadingParent || isLoadingTypes;

  // Get parent display name
  const parentDisplayName = useMemo(() => {
    if (!parentHardwareData) return parentHardwareId;
    return (
      parentHardwareData.label ??
      parentHardwareData.mac ??
      parentHardwareData.vendorId ??
      parentHardwareData.number?.toString() ??
      parentHardwareData.id
    );
  }, [parentHardwareData, parentHardwareId]);

  return (
    <PageContainer isLoading={isLoading}>
      <CardView
        content={
          <Stack px={2} pb={2}>
            <MovaticFieldItemLink
              label="Hardware Parent"
              onClick={goBack}
              content={parentDisplayName}
            />
            <MovaticFieldGroupSelect
              label="Hardware Child Type"
              description="Select the hardware type"
              name={'hardware_type_id'}
              optionNodes={hardwareTypesOptions.map((option, index) => (
                <MenuItem key={index} value={option.value}>
                  {option.name}
                </MenuItem>
              ))}
              value={hardwareTypeId}
              onChange={onHardwareTypeChange}
              hasError={hardwareTypeValidation.result === 'error'}
              errorMessage={hardwareTypeValidation.help}
              id="hardware_type_id"
            />

            {hardwareTypeId && (
              <>
                <MovaticFieldGroupSelect
                  label="Hardware Child Product"
                  description="Select the hardware product"
                  name={'hardware-product-id'}
                  optionNodes={hardwareProductsOptions.map((option, index) => (
                    <MenuItem key={index} value={option.value}>
                      {option.name}
                    </MenuItem>
                  ))}
                  value={hardwareProductId}
                  onChange={onHardwareProductChange}
                  id="hardware-product-id"
                />

                <MovaticFieldGroupSelect
                  label="Search Child By"
                  description="Select the search option"
                  name={'search-id'}
                  optionNodes={hardwareSearchFilters.map((option, index) => (
                    <MenuItem key={index} value={option.value}>
                      {option.name}
                    </MenuItem>
                  ))}
                  value={searchFilter}
                  onChange={handleSearchFilterChange}
                  hasError={searchFilterValidation.result === 'error'}
                  errorMessage={searchFilterValidation.help}
                  id="search-id"
                />
              </>
            )}

            {hardwareTypeId && searchFilter && searchFilter.length > 0 && (
              <MovaticFieldGroupAsyncSelect
                isLoading={isLoadingHardware}
                label="Hardware Child"
                description="Select the hardware child"
                name={'hardware-child-id'}
                open={open}
                onOpen={() => {
                  setOpen(true);
                }}
                onClose={() => {
                  setOpen(false);
                }}
                isOptionEqualToValue={(
                  option: HardwareGraphQL,
                  value: HardwareGraphQL
                ) => option.id === value.id}
                getOptionText={getOptionText}
                options={filteredHardwareOptions}
                onChange={(
                  _event: React.SyntheticEvent,
                  newValue: HardwareGraphQL | null
                ) => {
                  handleChildSelected(newValue);
                }}
                handleSearch={handleSearch}
                hasError={hardwareChildValidation.result === 'error'}
                errorMessage={hardwareChildValidation.help}
              />
            )}
          </Stack>
        }
        title="Add Hardware Assignment"
        headerActions={
          <>
            <IconButtonMenu
              buttons={[
                {
                  label: 'Cancel',
                  onClick: goBack,
                  startIcon: <CloseIcon />,
                },
                {
                  label: 'Submit',
                  onClick: createAssignment,
                  startIcon: <SaveIcon />,
                },
              ]}
            />
          </>
        }
      />
    </PageContainer>
  );
}

export default AddHardwareAssignment;
